import { Box, Button, Drawer, Stack, useTheme } from "@mui/material";
import { useMatch, useNavigate } from "react-router";
import AccountIconButton from "../molecules/AccountIconButton";
import Authorized from "../atoms/Authorized";
import Unauthorized from "../atoms/Unauthorized";
import appConfig from "../../config/app-config";
import MenuButton from "../atoms/MenuButton";
import { useMemo, useRef } from "react";
import { useGlobalContext } from "../../stores/Global";
import useBreakpoint from "../../hooks/useBreakpoint";
import MainSideBar, { sideBarWidth } from "./MainSideBar";
import LanguageSwitcher from "../molecules/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import NotificationButton from "./NotificationButton";
import { useBrandingStore } from "../../stores/Branding";

export default function NavBar() {
  const { t } = useTranslation("Navigation");
  const { context, setContext } = useGlobalContext();
  const breakpoint = useBreakpoint();
  const anchorEl = useRef<any>(null);
  const navigate = useNavigate();
  const match = useMatch("/:page/*");
  const page = match?.params.page;
  const theme = useTheme();
  const branding = useBrandingStore((s) => s.branding);

  const getColor = (value: string) => {
    if (page === value) return theme.palette.primary.light;
    else return theme.palette.text.disabled;
  };

  const getBgColor = (value: string) => {
    if (page === value) return theme.palette.background.paper;
    else return undefined;
  };

  const imageSrc = useMemo(() => {
    if (!branding.logo.image)
      return require("../../assets/logo-no-background.png");
    else return branding.logo.image;
  }, [branding.logo.image]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        px: 2,
        width: 1,
        height: 76,
        bgcolor: "background.default",
        boxShadow: "0px 4px 20px 0px #0000001A",
        boxSizing: "border-box",
        position: "relative",
        zIndex: context.sidebar && !breakpoint.md ? 1500 : undefined,
      }}
    >
      {/* Left: Logo */}
      <Stack flexShrink={0} justifyContent="center" alignItems="center">
        <img
          src={imageSrc}
          style={{
            height: 76,
            width: 110,
            cursor: "pointer",
            objectFit: "cover",
            overflow: "hidden",
          }}
          onClick={() => window.open(branding?.logo?.link, "_blank")}
        />
      </Stack>

      {/* Center Nav and Right Side */}
      {breakpoint.md ? (
        <>
          {/* Center: Nav (absolutely centered) */}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1,
            }}
          >
            <Button
              onClick={() => navigate("/home")}
              sx={{
                px: 4,
                py: 2,
                color: getColor("home"),
                bgcolor: getBgColor("home"),
              }}
            >
              {t("Home")}
            </Button>

            <Authorized role="user">
              <Button
                onClick={() => navigate("/my-tickets")}
                sx={{
                  px: 4,
                  py: 2,
                  color: getColor("my-tickets"),
                  bgcolor: getBgColor("my-tickets"),
                }}
              >
                {t("MyTickets")}
              </Button>
            </Authorized>

            <Authorized role="agent">
              <Button
                onClick={() => navigate("/m/dashboard")}
                sx={{
                  px: 4,
                  py: 2,
                  color: getColor("m"),
                  bgcolor: getBgColor("m"),
                }}
              >
                {t("Management")}
              </Button>
            </Authorized>

            <Unauthorized>
              <Button
                onClick={() => navigate("/login")}
                sx={{
                  px: 4,
                  py: 2,
                  color: getColor("login"),
                  bgcolor: getBgColor("login"),
                }}
              >
                {t("Login")}
              </Button>
              <Button
                onClick={() => navigate("/register")}
                sx={{
                  px: 4,
                  py: 2,
                  color: getColor("register"),
                  bgcolor: getBgColor("register"),
                }}
              >
                {t("Register")}
              </Button>
            </Unauthorized>
          </Stack>

          {/* Right: Actions */}
          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            sx={{ ml: "auto", mr: 1 }}
          >
            <Authorized role="user">
              {context.user && <NotificationButton />}
              <AccountIconButton />
            </Authorized>
          </Stack>
        </>
      ) : (
        <>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            justifyContent="flex-end"
            sx={{ ml: "auto" }}
          >
            {context.user && <NotificationButton />}
            <MenuButton
              ref={anchorEl}
              onClick={() =>
                setContext({ ...context, sidebar: !context.sidebar })
              }
            />
          </Stack>

          <Drawer
            open={context.sidebar}
            onClose={() => setContext({ ...context, sidebar: false })}
            elevation={4}
            anchor="right"
            variant="temporary"
            sx={{
              transitionDuration: "200ms",
              width: context.sidebar ? sideBarWidth : 0,
              flexShrink: 0,
              [`& .MuiDrawer-Paper`]: {
                height: "100%",
                boxSizing: "border-box",
                background: (theme) =>
                  breakpoint.md
                    ? theme.palette.background.paper
                    : "transparent",
                borderRight: "none",
                position: "relative",
                width: sideBarWidth,
              },
            }}
          >
            <MainSideBar />
          </Drawer>
        </>
      )}
    </Stack>
  );
}
